import axios from "axios";
import KeycloakService from "../contexts/auth/keycloakService";

// Store reference for accessing Redux state
let store;
export const setStore = (storeInstance) => {
  store = storeInstance;
};

// Flag to prevent multiple simultaneous token refresh attempts
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token, null));
  refreshSubscribers = [];
};

const onTokenRefreshFailed = (error) => {
  refreshSubscribers.forEach((callback) => callback(null, error));
  refreshSubscribers = [];
};


// Request interceptor to add headers for SSO users
axios.interceptors.request.use(
  async (config) => {
    // Skip adding Authorization header for external URLs (e.g., Azure Blob Storage SAS URLs)
    const isExternalUrl = config.url && (
      config.url.includes(".blob.core.windows.net") ||
      config.url.includes(".amazonaws.com")
    );

    if (store && !isExternalUrl) {
      const state = store.getState();
      const isSSOAuthenticated = state?.auth?.isSSOAuthenticated;

      // Add Authorization header with Keycloak token for API Gateway
      if (isSSOAuthenticated) {
        // Get valid token (refreshes if needed)
        const token = await KeycloakService.getValidToken();
        if (token) {
          localStorage.setItem("kc_token", token);
          config.headers["Authorization"] = `Bearer ${token}`;
        } else {
          // Fallback to stored token (embed mode - Keycloak JS not initialized)
          const storedToken = localStorage.getItem("kc_token");
          if (storedToken) {
            config.headers["Authorization"] = `Bearer ${storedToken}`;
          }
        }
      }
    }

    // Embed mode fallback: if store not ready but token exists in localStorage
    if (!isExternalUrl && !config.headers["Authorization"]) {
      const storedToken = localStorage.getItem("kc_token");
      if (storedToken && window.location.pathname.startsWith("/embed")) {
        config.headers["Authorization"] = `Bearer ${storedToken}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Check for onboarding requirement FIRST - applies to both SSO and legacy auth
      // const errorData = error?.response?.data;
      // if (errorData?.requiresOnboarding) {
      //   // User requires onboarding - redirect to contact page
      //   // Prevent redirect loop if already on onboarding-contact page
      //   const currentPath = window.location.pathname;
      //   if (currentPath !== "/onboarding-contact") {
      //     const email = errorData?.email || '';
      //     window.location.href = `/onboarding-contact?email=${encodeURIComponent(email)}`;
      //   }
      //   return Promise.reject(error);
      // }

      // Check if using SSO
      const state = store?.getState();
      const isSSOAuthenticated = state?.auth?.isSSOAuthenticated;

      if (isSSOAuthenticated) {
        if (isRefreshing) {
          // Wait for token refresh to complete
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((token, refreshError) => {
              if (refreshError) {
                reject(refreshError);
                return;
              }
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              resolve(axios(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Try to refresh the token
          const newToken = await KeycloakService.refreshTokenIfNeeded(0);

          if (newToken) {
            localStorage.setItem("kc_token", newToken);
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            onTokenRefreshed(newToken);
            isRefreshing = false;
            return axios(originalRequest);
          }
        } catch (refreshError) {
          console.error("Token refresh failed on 401:", refreshError);
        }

        isRefreshing = false;
        onTokenRefreshFailed(error);
        // Token refresh failed, trigger re-login
        KeycloakService.login();
        return Promise.reject(error);
      }

      // Legacy auth - redirect to login page if not on a public page
      const currentPath = window.location.pathname;
      const isPasswordResetFlow = currentPath.startsWith("/resetPassword/") || currentPath.startsWith("/verifyAccount/") || currentPath === "/recovery" || currentPath === "/result-validator" || currentPath === "/onboarding-contact";
      if (currentPath !== "/" && !isPasswordResetFlow) {
        window.location.href = "/";
      }
    }

    return Promise.reject(error);
  }
);

const requestConfig = {
  url: "",
  method: "get", // default
  baseURL: "",
  transformRequest: [
    function transformRequest(data) {
      // Do whatever you want to transform the data
      return data;
    },
  ],
  transformResponse: [
    function transformResponse(data) {
      // Do whatever you want to transform the data
      return data;
    },
  ],
  headers: {},
  params: {},
  timeout: 330000,
  withCredentials: true,
  credentials: "include",
  responseType: "json", // default
  maxContentLength: 50000,
  validateStatus(status) {
    return status >= 200 && status < 300; // default
  },
  maxRedirects: 5, // default
};

export const getFromLocalStorage = (key) => {
  try {
    const serializedState = localStorage.getItem(key);
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    return undefined;
  }
};

export const saveToLocalStorage = (key, value) => {
  try {
    const serializedState = JSON.stringify(value);
    localStorage.setItem(key, serializedState);
  } catch (err) {
    // Ignoring write error as of now
  }
};

export const clearFromLocalStorage = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (err) {
    return false;
  }
};

function getRequestConfig(apiConfig) {
  let config = Object.assign({}, requestConfig);
  if (apiConfig) {
    config = Object.assign({}, requestConfig, apiConfig);
  }
  return config;
}

export const get = (url, params, apiConfig) => {
  const config = getRequestConfig(apiConfig);
  config.params = params;
  // const token = localStorage.getItem("token");
  // axios.defaults.headers.get["Authorization"] = `bearer ${token}`;
  const request = axios.get(url, config);
  return request;
};

export const post = (url, data, apiConfig = null) => {
  const config = getRequestConfig(apiConfig);
  // let postData = {};
  // postData = JSON.stringify(data);
  // const token = localStorage.getItem("token");
  //if (!apiConfig)
  axios.defaults.headers.post["Content-Type"] = "application/json";
  // if (token) {
  //   axios.defaults.headers.post["Authorization"] = `bearer ${token}`;
  // }
  const request = axios.post(url, JSON.stringify(data), config);
  return request;
};

export const put = (url, data) => {
  const config = getRequestConfig();
  // const token = localStorage.getItem("token");
  axios.defaults.headers.put["Content-Type"] = "application/json";
  // if (token) {
  //   axios.defaults.headers.put["Authorization"] = `bearer ${token}`;
  // }
  const request = axios.put(url, JSON.stringify(data), config);
  return request;
};

export const patch = (url, data) => {
  const config = getRequestConfig();
  config.headers["Content-Type"] = "application/json";
  const request = axios.patch(url, JSON.stringify(data), config);
  return request;
};

export const deleteResource = (url) => {
  const config = getRequestConfig();
  const request = axios.delete(url, config);
  return request;
};

export const postDCT = (url, data, apiConfig = null) => {
  const config = getRequestConfig(apiConfig);

  const request = axios.post(url, data, config);
  return request;
};

export const deleteItem = (url, data) => {
  const config = getRequestConfig();

  const finalConfig = {
    ...config,
    headers: {
      ...config.headers,
      "Content-Type": "application/json",
    },
  };

  if (data) {
    finalConfig.data = JSON.stringify(data);
  }

  return axios.delete(url, finalConfig);
};
